import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { formatDateIt, formatDateTimeIt, LEAD_RANKING_CLASS, LEAD_RANKING_LABEL } from "@/lib/format";
import { UserAvatar } from "@/components/UserAvatar";
import { sanitizeHtml } from "@/lib/sanitize";
import { Phone, Mail, MessageCircle, Rss, ChevronDown, ChevronRight, Plus, ArrowLeft, Pencil, Trash2, X, Search } from "lucide-react";
import NewClientModal from "@/components/NewClientModal";

const NOTE_ICONS = { telefono: Phone, email: Mail, generico: MessageCircle, social: Rss };

export default function ClientDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [client, setClient] = useState(null);
    const [notes, setNotes] = useState([]);
    const [noteTypes, setNoteTypes] = useState([]);
    const [projects, setProjects] = useState([]);
    const [categories, setCategories] = useState([]);
    const [collaborators, setCollaborators] = useState([]);
    const [users, setUsers] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [showEdit, setShowEdit] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    // Note composer/editor state
    const [showComposer, setShowComposer] = useState(false);
    const [editingNote, setEditingNote] = useState(null); // note obj when editing
    // Filters
    const [filterType, setFilterType] = useState("");
    const [filterQ, setFilterQ] = useState("");

    const loadNotes = async () => {
        const params = {};
        if (filterType) params.type_key = filterType;
        if (filterQ) params.q = filterQ;
        const { data } = await api.get(`/clients/${id}/notes`, { params });
        setNotes(data);
    };
    const load = async () => {
        const [c, nt, pr, cats, coll, u] = await Promise.all([
            api.get(`/clients/${id}`),
            api.get(`/settings/note-types`), api.get(`/clients/${id}/projects`),
            api.get(`/settings/categories`), api.get(`/collaborators`), api.get(`/users`),
        ]);
        setClient(c.data); setNoteTypes(nt.data);
        setProjects(pr.data); setCategories(cats.data);
        setCollaborators(coll.data); setUsers(u.data);
        await loadNotes();
    };
    useEffect(() => { load(); }, [id]);  // eslint-disable-line
    useEffect(() => { if (client) loadNotes(); }, [filterType, filterQ]);  // eslint-disable-line

    if (!client) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;

    const cat = categories.find((c) => c.id === client.category_id);
    const coll = collaborators.find((c) => c.id === client.collaborator_id);
    const usersById = Object.fromEntries(users.map((u) => [u.id, u]));
    const noteTypesByKey = Object.fromEntries(noteTypes.map((t) => [t.key, t]));

    const confirmDelete = async () => {
        await api.delete(`/clients/${id}`);
        nav("/clienti");
    };

    const deleteNote = async (nid) => {
        if (!window.confirm("Eliminare questa nota?")) return;
        await api.delete(`/clients/${id}/notes/${nid}`);
        loadNotes();
    };

    return (
        <div className="p-6" data-testid="client-detail-page">
            <div className="flex items-center gap-3 mb-4">
                <Link to="/clienti" className="text-neutral-500 hover:text-neutral-800"><ArrowLeft size={16} /></Link>
                <div>
                    <div className="text-xs text-neutral-500">Cliente</div>
                    <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">{client.first_name} {client.last_name}</h1>
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded border ${LEAD_RANKING_CLASS[client.lead_ranking] || ""}`}>{LEAD_RANKING_LABEL[client.lead_ranking]}</span>
                    <button onClick={() => setShowEdit(true)} data-testid="edit-client-btn" className="text-sm border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 rounded px-2.5 py-1 inline-flex items-center gap-1">
                        <Pencil size={13} /> Modifica
                    </button>
                    <button onClick={() => setShowDelete(true)} data-testid="delete-client-btn" className="text-sm border border-red-200 bg-white text-red-600 hover:bg-red-50 rounded px-2.5 py-1 inline-flex items-center gap-1">
                        <Trash2 size={13} /> Elimina
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-0 bg-white border border-neutral-200 rounded-md overflow-hidden">
                <div className="md:col-span-4 border-r border-neutral-200 p-5" data-testid="client-left-column">
                    <Section title="Anagrafica">
                        <Row label="Ruolo">{client.role || "—"}</Row>
                        <Row label="Azienda">{client.company || "—"}</Row>
                        <Row label="Email">{client.email ? <a className="text-neutral-800 hover:underline" href={`mailto:${client.email}`}>{client.email}</a> : "—"}</Row>
                        <Row label="Telefono">{client.phone || "—"}</Row>
                        <Row label="Compleanno">{formatDateIt(client.birthday)}</Row>
                    </Section>
                    <Section title="Classificazione">
                        <Row label="Categoria">
                            {cat ? <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: cat.color }} /> {cat.name}</span> : "—"}
                        </Row>
                        <Row label="Lead ranking">
                            <span className={`inline-flex text-xs px-1.5 py-0.5 rounded border ${LEAD_RANKING_CLASS[client.lead_ranking] || ""}`}>{LEAD_RANKING_LABEL[client.lead_ranking]}</span>
                        </Row>
                        <Row label="Procurato da">{coll ? <Link className="text-neutral-800 hover:underline" to={`/collaboratori/${coll.id}`}>{coll.name}</Link> : "—"}</Row>
                    </Section>
                    {client.social && Object.keys(client.social).length > 0 && (
                        <Section title="Social">
                            {Object.entries(client.social).map(([k, v]) => v && <Row key={k} label={k}>{v}</Row>)}
                        </Section>
                    )}
                    {client.base_notes && (
                        <Section title="Note base">
                            <div className="text-sm text-neutral-700 whitespace-pre-line">{client.base_notes}</div>
                        </Section>
                    )}
                    <Section title="Progetti collegati">
                        {projects.length === 0 && <div className="text-sm text-neutral-400">Nessun progetto.</div>}
                        <ul className="space-y-1.5">
                            {projects.map((p) => (
                                <li key={p.id}>
                                    <Link to={`/progetti/${p.id}`} className="text-sm text-neutral-800 hover:underline">
                                        {p.title} <span className="text-xs text-neutral-500">· {p.project_type === "continuous" ? "Continuativo" : "Spot"}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </Section>
                </div>

                <div className="md:col-span-8 p-5 bg-neutral-50/50" data-testid="client-timeline-column">
                    <div className="flex items-center justify-between mb-3">
                        <div className="font-display font-semibold text-neutral-800">Timeline note</div>
                        <button onClick={() => { setEditingNote(null); setShowComposer(true); }} data-testid="add-note-btn"
                            className="text-sm bg-neutral-900 text-white px-2.5 py-1 rounded inline-flex items-center gap-1 hover:bg-neutral-800">
                            <Plus size={13} /> Nota
                        </button>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-3">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
                            <input value={filterQ} onChange={(e) => setFilterQ(e.target.value)} placeholder="Cerca nel contenuto…"
                                data-testid="notes-search"
                                className="w-full pl-7 pr-3 py-1 text-xs border border-neutral-200 rounded bg-white" />
                        </div>
                        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="notes-filter-type"
                            className="text-xs border border-neutral-200 rounded px-2 py-1 bg-white">
                            <option value="">Tutti i tipi</option>
                            {noteTypes.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                        </select>
                    </div>

                    {showComposer && (
                        <NoteComposer
                            noteTypes={noteTypes}
                            initial={editingNote}
                            onClose={() => { setShowComposer(false); setEditingNote(null); }}
                            onSaved={() => { setShowComposer(false); setEditingNote(null); loadNotes(); }}
                            clientId={id}
                        />
                    )}

                    {notes.length === 0 && <div className="bg-white border border-dashed border-neutral-200 rounded-md p-6 text-center text-sm text-neutral-400" data-testid="notes-empty">Nessuna nota trovata.</div>}

                    <ul className="space-y-4">
                        {notes.map((n) => {
                            const t = noteTypesByKey[n.type_key];
                            const Icon = NOTE_ICONS[n.type_key] || MessageCircle;
                            const isOpen = expanded[n.id] || false;
                            const author = usersById[n.author_id];
                            const displayDate = n.event_date || n.created_at;
                            return (
                                <li key={n.id} className="timeline-item" data-testid={`note-${n.id}`}>
                                    <div className="timeline-dot" style={{ borderColor: t?.color }}><Icon size={10} style={{ color: t?.color }} /></div>
                                    <div className="bg-white border border-neutral-200 rounded-md">
                                        <div className="flex items-center gap-3 px-3 py-2">
                                            <button onClick={() => setExpanded({ ...expanded, [n.id]: !isOpen })} className="flex items-center gap-2 flex-1 text-left min-w-0">
                                                {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                                <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: (t?.color || "#64748b") + "22", color: t?.color || "#334155" }}>{t?.name || n.type_key}</span>
                                                <span className="text-xs text-neutral-500">{n.event_date ? formatDateIt(displayDate) : formatDateTimeIt(displayDate)}</span>
                                                {!isOpen && <span className="text-sm text-neutral-600 truncate flex-1 ml-2">{n.content.replace(/<[^>]+>/g, "").slice(0, 100)}</span>}
                                            </button>
                                            <UserAvatar user={author} size={22} />
                                            <button onClick={() => { setEditingNote(n); setShowComposer(true); }} data-testid={`edit-note-${n.id}`} className="text-neutral-400 hover:text-neutral-700"><Pencil size={12} /></button>
                                            <button onClick={() => deleteNote(n.id)} data-testid={`delete-note-${n.id}`} className="text-neutral-400 hover:text-red-600"><Trash2 size={12} /></button>
                                        </div>
                                        {isOpen && <div className="px-3 pb-3 pt-1"><div className="rich-content text-sm text-neutral-800" dangerouslySetInnerHTML={{ __html: sanitizeHtml(n.content) }} /></div>}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>

            {showEdit && (
                <NewClientModal categories={categories} collaborators={collaborators} initial={client}
                    onClose={() => setShowEdit(false)}
                    onCreated={() => { setShowEdit(false); load(); }} />
            )}

            {showDelete && (
                <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDelete(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md shadow-lg w-full max-w-md" data-testid="delete-client-modal">
                        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
                            <div className="font-display font-semibold">Eliminare il cliente?</div>
                            <button onClick={() => setShowDelete(false)}><X size={16} /></button>
                        </div>
                        <div className="p-5 text-sm text-neutral-700">
                            <p>Stai per eliminare <b>{client.first_name} {client.last_name}</b>.</p>
                            {projects.length > 0 ? (
                                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-amber-900" data-testid="delete-client-warning">
                                    Questo cliente ha <b>{projects.length}</b> progett{projects.length === 1 ? "o" : "i"} collegat{projects.length === 1 ? "o" : "i"}. I progetti resteranno in archivio, ma perderanno il riferimento al cliente.
                                </div>
                            ) : (
                                <div className="mt-3 text-neutral-500">Nessun progetto collegato.</div>
                            )}
                        </div>
                        <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                            <button onClick={() => setShowDelete(false)} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                            <button onClick={confirmDelete} data-testid="confirm-delete-client" className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700">Elimina definitivamente</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function NoteComposer({ noteTypes, initial, onClose, onSaved, clientId }) {
    const [type, setType] = useState(initial?.type_key || noteTypes[0]?.key || "");
    const [content, setContent] = useState(initial?.content || "");
    const [eventDate, setEventDate] = useState(initial?.event_date || new Date().toISOString().slice(0, 10));
    const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!content.trim()) return;
        setBusy(true); setErr("");
        try {
            const payload = { type_key: type, content, event_date: eventDate || null };
            if (initial?.id) await api.patch(`/clients/${clientId}/notes/${initial.id}`, payload);
            else await api.post(`/clients/${clientId}/notes`, payload);
            onSaved();
        } catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
    };
    return (
        <form onSubmit={submit} data-testid="note-composer" className="bg-white border border-neutral-200 rounded-md p-3 mb-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <select value={type} onChange={(e) => setType(e.target.value)} data-testid="note-type" className="text-sm border border-neutral-200 rounded px-2 py-1">
                    {noteTypes.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                </select>
                <label className="text-xs text-neutral-600 inline-flex items-center gap-1">
                    Data
                    <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} data-testid="note-date"
                        className="text-xs border border-neutral-200 rounded px-1.5 py-0.5" />
                </label>
            </div>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Scrivi la nota…" rows={4}
                data-testid="note-content"
                className="w-full text-sm border border-neutral-200 rounded p-2" />
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2">{err}</div>}
            <div className="flex justify-end gap-2 mt-2">
                <button type="button" onClick={onClose} className="text-sm text-neutral-500 px-2 py-1">Annulla</button>
                <button type="submit" disabled={busy} data-testid="save-note-btn" className="text-sm bg-neutral-900 text-white px-3 py-1 rounded">
                    {busy ? "Salvataggio…" : (initial ? "Aggiorna" : "Salva")}
                </button>
            </div>
        </form>
    );
}

function Section({ title, children }) { return <div className="mb-5"><div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">{title}</div><div className="space-y-1.5">{children}</div></div>; }
function Row({ label, children }) { return <div className="flex items-baseline text-sm"><div className="w-24 text-xs text-neutral-500 shrink-0">{label}</div><div className="text-neutral-800 min-w-0 break-words">{children}</div></div>; }
